import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useSettings } from "@/context/SettingsContext";
import { TRANSPARENT_LOGO, BRAND_GREEN } from "@/lib/brand";

const LOGO = TRANSPARENT_LOGO;

export default function Home() {
  const { settings } = useSettings();
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [oneOfOne, setOneOfOne] = useState([]);
  const [collections, setCollections] = useState([]);
  const [archive, setArchive] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data));
    api.get("/products", { params: { is_new_arrival: true, limit: 4 } }).then((r) => setNewArrivals(r.data));
    api.get("/products", { params: { limit: 100 } }).then((r) => setOneOfOne(r.data.filter((p) => p.inventory_type === "one_of_one" && p.product_status === "active").slice(0, 4)));
    api.get("/collections").then((r) => setCollections(r.data));
    api.get("/archive", { params: { limit: 3 } }).then((r) => setArchive(r.data));
  }, []);

  const hero = settings?.hero || {};
  const sections = settings?.sections || {};
  const seo = settings?.seo || {};
  const archiveCfg = settings?.archive || {};

  useEffect(() => {
    if (seo.home_title) document.title = seo.home_title;
    // Owner-editable social/link-preview meta (client-side, for live users + JS-capable crawlers).
    // Blank Social Preview values fall back to Home SEO Title / Home Meta Description.
    const socialTitle = (seo.social_title || "").trim() || (seo.home_title || "").trim();
    const socialDesc = (seo.social_description || "").trim() || (seo.home_description || "").trim();
    const setMeta = (attr, name, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    if (socialDesc) setMeta("name", "description", socialDesc);
    if (socialTitle) { setMeta("property", "og:title", socialTitle); setMeta("name", "twitter:title", socialTitle); }
    if (socialDesc) { setMeta("property", "og:description", socialDesc); setMeta("name", "twitter:description", socialDesc); }
    return () => {};
  }, [seo.home_title, seo.home_description, seo.social_title, seo.social_description]);

  return (
    <div data-testid="home-page">
      {/* Hero — brand-green background so transparent logo blends in seamlessly */}
      <section className="relative overflow-hidden" style={{ background: BRAND_GREEN }} data-testid="hero-section">
        <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
          backgroundImage: `url('${hero.background_image || "https://images.unsplash.com/photo-1607028215544-15380c240287?w=1600&q=80"}')`,
          backgroundSize: "cover", backgroundPosition: "center",
          filter: "sepia(0.5) hue-rotate(60deg) brightness(0.35)"
        }} />
        <div className="relative max-w-5xl mx-auto px-6 py-16 sm:py-24 text-center">
          {/* Hero logo/media slot — future 3D animation will mount here when animated_logo_enabled=true */}
          {hero.show_logo !== false && (
            <div data-testid="hero-logo-slot" data-animated={String(!!hero.animated_logo_enabled)}>
              <img src={hero.logo_asset || LOGO} alt="Golden Moth Market — Beautiful Things Find Their Way Back"
                className="h-56 sm:h-80 md:h-96 w-auto mx-auto float-slow" />
            </div>
          )}
          {/* Only render eyebrow / headline when the logo is hidden — the logo itself carries brand + tagline */}
          {hero.show_logo === false && (
            <>
              <p className="font-caption text-gold text-xs sm:text-sm mb-3">Est. Under the Crescent</p>
              {hero.headline && (
                <h1 className="font-display text-4xl sm:text-6xl text-parchment leading-[1.05] max-w-3xl mx-auto" data-testid="hero-headline">
                  {hero.headline}
                </h1>
              )}
              {hero.subheadline && (
                <p className="font-serif-italic text-gold text-xl sm:text-2xl mt-4" data-testid="hero-subheadline">{hero.subheadline}</p>
              )}
            </>
          )}
          {hero.supporting_copy && (
            <p className="text-parchment/75 max-w-xl mx-auto mt-4 sm:mt-6 text-base sm:text-lg italic">{hero.supporting_copy}</p>
          )}
          {(hero.primary_cta_text || hero.secondary_cta_text) && (
            <div className="mt-8 sm:mt-10 flex flex-wrap gap-3 sm:gap-4 justify-center">
              {hero.primary_cta_text && (
                <Link to={hero.primary_cta_link || "/shop"} data-testid="hero-shop-btn" className="btn-gold">{hero.primary_cta_text}</Link>
              )}
              {hero.secondary_cta_text && (
                <Link to={hero.secondary_cta_link || "/archive"} data-testid="hero-archive-btn" className="btn-outline">{hero.secondary_cta_text}</Link>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-brass-soft py-4 overflow-hidden bg-[color:var(--forest-2)]">
        <div className="marquee-track flex gap-16 whitespace-nowrap font-caption text-brass text-sm w-max">
          {Array(3).fill(null).map((_, i) => (
            <span key={i} className="flex items-center gap-16">
              <span>✦ Rare Found Objects</span><span>☾ Celestial Jewelry</span><span>✿ Botanical Ephemera</span>
              <span>✦ Antique Curiosities</span><span>☾ One of One</span><span>✿ Apothecary</span>
            </span>
          ))}
        </div>
      </section>

      {/* New Arrivals */}
      {newArrivals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center mb-12">
            <p className="font-caption text-brass text-xs mb-2">{sections.new_arrivals_subhead || "Newly found treasures"}</p>
            <h2 className="font-display text-3xl sm:text-5xl text-gold">{sections.new_arrivals_heading || "Fresh Arrivals"}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {newArrivals.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Featured Finds */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center mb-12">
            <p className="font-caption text-brass text-xs mb-2">{sections.featured_subhead || "Curated This Moon"}</p>
            <h2 className="font-display text-3xl sm:text-5xl text-gold" data-testid="featured-heading">{sections.featured_heading || "Featured Finds"}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Collections */}
      <section className="bg-[color:var(--forest-2)] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-caption text-brass text-xs mb-2">Wander</p>
            <h2 className="font-display text-3xl sm:text-5xl text-gold">{sections.collections_heading || "Shop by Collection"}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {collections.map((c) => (
              <Link key={c.id} to={`/shop?collection=${c.slug}`} data-testid={`collection-${c.slug}`}
                className="curio-card block p-8 text-center corner-frame">
                <div className="font-display text-2xl text-gold mb-2">{c.name}</div>
                <div className="text-parchment/70 text-sm italic">{c.description}</div>
                <div className="font-caption text-brass text-[10px] mt-4">Explore →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* One of One */}
      {oneOfOne.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center mb-12">
            <p className="font-caption text-brass text-xs mb-2">{sections.one_of_one_subhead || "Once these travel home, they're gone"}</p>
            <h2 className="font-display text-3xl sm:text-5xl text-gold">{sections.one_of_one_heading || "One of One"}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {oneOfOne.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Brand Story */}
      <section className="bg-[color:var(--forest-2)] py-16 sm:py-24 border-y border-brass-soft">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="font-caption text-brass text-xs mb-2">The Proprietor</p>
          <h2 className="font-display text-3xl sm:text-4xl text-gold mb-6">{sections.story_heading || "A Small Note on Provenance"}</h2>
          <p className="text-parchment/80 text-lg italic leading-relaxed">
            {sections.story_body || "Every object here has drifted through hands and homes before it reached this cabinet."}
          </p>
        </div>
      </section>

      {/* Found New Home Preview */}
      {archive.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center mb-12">
            <p className="font-caption text-brass text-xs mb-2">Archive</p>
            <h2 className="font-display text-3xl sm:text-5xl text-gold">{sections.archive_preview_heading || archiveCfg.page_heading || "Found a New Home"}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
            {archive.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          <div className="text-center mt-10">
            <Link to="/archive" className="btn-outline">Browse the Archive</Link>
          </div>
        </section>
      )}
    </div>
  );
}
